import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Car, Clock, Mail, Phone, CheckCircle2, AlertCircle, LogOut } from 'lucide-react';

export default function PendingApproval() {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4" data-testid="pending-approval-page">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">Application Pending</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center text-gray-600">
            <p className="mb-2">Thank you for applying to become a</p>
            <p className="text-lg font-semibold text-blue-600 capitalize">
              {user?.user_type === 'roadside_tech' ? 'Roadside Technician' : 'Mobile Mechanic'}
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Admin Review Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  Your application is being reviewed by our team. This typically takes 1-2 business days.
                </p>
              </div>
            </div>
          </div>

          {/* Application Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Your Application</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm">Name: {user?.name}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm">{user?.email}</span>
              </div>
              {user?.phone && (
                <div className="flex items-center gap-2 text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{user.phone}</span>
                </div>
              )}
              {user?.skills?.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500 mb-1">Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {user.skills.map((skill, idx) => (
                      <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {user?.certifications?.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500 mb-1">Certifications:</p>
                  <div className="flex flex-wrap gap-1">
                    {user.certifications.map((cert, idx) => (
                      <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* What happens next */}
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">What's Next?</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">1</span>
                <span>Our team reviews your application and credentials</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">2</span>
                <span>You'll receive an email notification once approved</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">3</span>
                <span>Complete your profile setup and start accepting jobs!</span>
              </li>
            </ul>
          </div>

          <div className="pt-4 border-t space-y-3">
            <Link to="/">
              <Button variant="outline" className="w-full">
                <Car className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </Link>
            <Button variant="ghost" className="w-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
